import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MarketBanner, MarketCheckbox, MarketRow } from '@market/react';
import {
  buildCartLink,
  buildDashboardLink,
  DASHBOARD_SUBPATHS,
} from 'utilities/links';
import { usePaymentContext } from 'typescript/context/payment_context';
import {
  SUBSCRIPTION_DISCLOSURE_MODE,
  useSubscriptionDisclosure,
} from 'typescript/apps/checkout_v3/hooks/use_subscription_disclosure';

export const OPT_IN_DISCLAIMER_TEST_ID = 'subscription-disclosure-opt-in';
export const CONSENT_CHECKBOX_TEST_ID = 'subscription-consent-checkbox';
export const CONSENT_ERROR_TEST_ID = 'subscription-consent-error';

const I18N_BASE = 'checkout.payment_v3.subscriptionDisclosure';

interface SubscriptionDisclosureProps {
  className?: string;
}

/**
 * Variant-specific subscription disclosure, rendered directly above the submit
 * CTA. Modes are documented on SUBSCRIPTION_DISCLOSURE_MODE; the opt_out
 * submit gate lives in useSubmitOrder.
 */
const SubscriptionDisclosure = ({
  className = '',
}: SubscriptionDisclosureProps) => {
  const { t } = useTranslation();
  const { mode, price, hasTrial, trialDays, trialEndDate, consentKey } =
    useSubscriptionDisclosure();
  const {
    subscriptionConsentGiven,
    setSubscriptionConsentGiven,
    subscriptionConsentError,
    setSubscriptionConsentError,
  } = usePaymentContext();

  const errorRef = useRef<HTMLDivElement>(null);

  // consentKey change = the disclosed terms changed; drop any stale consent.
  useEffect(() => {
    setSubscriptionConsentGiven(false);
    setSubscriptionConsentError(false);
  }, [consentKey, setSubscriptionConsentGiven, setSubscriptionConsentError]);

  useEffect(() => {
    if (subscriptionConsentError) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [subscriptionConsentError]);

  if (mode === SUBSCRIPTION_DISCLOSURE_MODE.NONE) {
    return null;
  }

  const copyKey = `${I18N_BASE}.${mode === SUBSCRIPTION_DISCLOSURE_MODE.OPT_IN ? 'optIn' : 'optOut'}.${hasTrial ? 'trial' : 'standard'}`;
  const copy = (
    <Trans
      t={t}
      i18nKey={copyKey}
      values={{ price, trialDays, trialEndDate }}
      components={{
        accountSettingsLink: (
          <a
            href={buildDashboardLink(DASHBOARD_SUBPATHS.ACCOUNT_PRICING)}
            target="_blank"
            rel="noopener noreferrer"
          />
        ),
      }}
    />
  );

  if (mode === SUBSCRIPTION_DISCLOSURE_MODE.OPT_IN) {
    return (
      <p
        className={`paragraph-20 ${className}`}
        data-testid={OPT_IN_DISCLAIMER_TEST_ID}
      >
        {copy}
      </p>
    );
  }

  return (
    <div className={className}>
      <MarketRow
        data-testid={CONSENT_CHECKBOX_TEST_ID}
        control-position="leading"
        selected={subscriptionConsentGiven}
        onMarketRowSelected={() => {
          setSubscriptionConsentGiven(true);
          setSubscriptionConsentError(false);
        }}
        onMarketRowDeselected={() => {
          setSubscriptionConsentGiven(false);
        }}
      >
        <label slot="label" className="paragraph-20">
          {copy}
        </label>
        <MarketCheckbox
          slot="control"
          {...(subscriptionConsentGiven && { checked: true })}
          {...(subscriptionConsentError && { invalid: true })}
        ></MarketCheckbox>
      </MarketRow>
      {subscriptionConsentError && (
        <div ref={errorRef}>
          <MarketBanner variant="critical" data-testid={CONSENT_ERROR_TEST_ID}>
            <span>
              <Trans
                t={t}
                i18nKey={`${I18N_BASE}.error`}
                components={{
                  cartLink: <a href={buildCartLink()} />,
                }}
              />
            </span>
          </MarketBanner>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDisclosure;
